import config from 'config';

import {
  getCioSortOrder,
  getPreFilterExpressions,
  getSanitizedFilterInputsForCio,
  isolateSizeFilters,
} from '~/grid/utils/cioUtils';

import { LEAN_FACET_QUERY_RESULTS_PER_PAGE } from '../constants';

/**
 * @function getCioLeanFacetResults
 * @description Fetches facet data from Constructor.io with minimal product payload.
 * Uses resultsPerPage=1 and fmt_options[fields]=id to minimize bandwidth.
 * Supports both browse and search query types.
 * @param {Object} params
 * @param {Object} params.cioClient - Constructor.io client instance
 * @param {'browse'|'search'} params.queryType - Whether to use browse or search API
 * @param {String} [params.cioFilterName] - Browse filter name (browse only)
 * @param {String} [params.cioFilterValue] - Browse filter value (browse only)
 * @param {String} [params.searchQuery] - Search query string (search only)
 * @param {Boolean} [params.isElite] - Is the user an elite member
 * @param {Boolean} [params.isVip] - Is the user a VIP member
 * @param {Object} [params.selectedUrlFilters] - Selected filters to apply
 * @param {String} [params.selectedSortOption] - Selected sort option
 * @param {String} [params.tld] - Top-level domain
 * @param {Boolean} [params.removeOriginFlag] - Flag to remove origin restricted products
 * @param {Boolean} [params.isSecondarySite] - Flag to indicate if the site is secondary
 * @param {Boolean} [params.isSaleGrid] - Flag to indicate if the grid is a sale grid
 * @param {Array} [params.excludeTagIds] - Array of tag ids to exclude
 * @param {Array} [params.excludeFplCodes] - Array of FPL global codes to exclude
 * @param {Boolean} [params.surfaceHiddenFacets] - Flag to indicate if hidden facets should be surfaced (search only)
 * @param {Object} [params.hiddenFacetFilters] - Object containing hidden facet filters
 * @return {Object} Object containing { facets: Facet[] }
 */
const getCioLeanFacetResults = async ({
  cioClient,
  queryType = 'browse',
  // browse-specific
  cioFilterName,
  cioFilterValue,
  // search-specific
  searchQuery,
  // shared
  isElite = false,
  isVip = false,
  selectedUrlFilters = {},
  selectedSortOption,
  tld = 'com',
  removeOriginFlag = false,
  isSecondarySite = false,
  isSaleGrid = false,
  excludeTagIds = [],
  excludeFplCodes = [],
  surfaceHiddenFacets = false,
  hiddenFacetFilters = {},
}) => {
  const staticCurrentDate = new Date();
  const hiddenFacets = config.get('public.brand.constructor.hiddenFacets');

  const { sizeFilters, nonSizeFilters } =
    isolateSizeFilters(selectedUrlFilters);

  const sanitizedFilters = getSanitizedFilterInputsForCio(nonSizeFilters);

  const filters = {
    ...sanitizedFilters,
    ...hiddenFacetFilters,
  };

  const sharedParams = {
    resultsPerPage: LEAN_FACET_QUERY_RESULTS_PER_PAGE,
    page: 1,
    preFilterExpression: getPreFilterExpressions({
      isElite,
      isVip,
      dateNowInMs: staticCurrentDate.getTime(),
      shouldShowWarehouseProducts: isSecondarySite,
      shouldHideOriginRestricted: removeOriginFlag,
      selectedSizeFilters: sizeFilters,
      tld,
      ...(queryType === 'search' ? { isSearch: true } : {}),
      ...(queryType === 'browse'
        ? { autoExcludeClearanceFplIds: !isSaleGrid }
        : {}),
      excludeTagIds,
      excludeFplCodes,
    }),
    filters,
    sortBy: selectedSortOption,
    sortOrder: getCioSortOrder(selectedSortOption),
    fmtOptions: {
      fields: ['id'],
    },
  };

  // Mirror getCioSearchResults: only surface hidden facets under the flag for
  // search. Browse always surfaces them (matches getCioBrowseResults), so the
  // lean query must do the same to keep disabled-state math aligned with the
  // main query.
  if (queryType === 'browse' || (surfaceHiddenFacets && hiddenFacets?.length)) {
    sharedParams.hiddenFacets = hiddenFacets;
  }

  let results;

  if (queryType === 'search') {
    results = await cioClient.search.getSearchResults(
      searchQuery,
      sharedParams
    );
  } else {
    results = await cioClient.browse.getBrowseResults(
      cioFilterName,
      cioFilterValue,
      sharedParams
    );
  }

  // Apply the same per-brand facet blacklist used in the main product-query
  // pipeline so all consumers of lean facets (baseFacets, dynamicFacets) see a
  // consistent set.
  const facetBlacklist =
    config.get('public.brand.constructor.facetBlacklist') || [];
  const facets = (results?.response?.facets || []).filter(
    (facet) => !facetBlacklist.includes(facet.name)
  );

  return {
    facets,
    totalNumResults: results?.response?.total_num_results ?? 0,
  };
};

export default getCioLeanFacetResults;
